import {
	Box,
	IconButton,
	InputAdornment,
	InputLabel,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';
import EmergencyIcon from '@mui/icons-material/Emergency';
import Accessory, { AccessoryMaterial } from 'src/types/shared/accessory';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import removeStartingZero from 'src/pages/quotations/views/blocks/utils/removeStartingZero';

const calculationTypesMap = {
	'Per Accessory': 'accessory',
	'Per Square Foot': 'sq ft',
	'Per Linear Foot': 'linear ft',
};
const AccessoryMaterialDataCard: React.FC<{
	index: number;
	accessoryMaterialData: AccessoryMaterial;
	setAccessoryData: Dispatch<SetStateAction<Accessory>>;
	accessoryData: Accessory;
}> = ({ index, accessoryMaterialData, setAccessoryData, accessoryData }) => {
	const isMaterial = accessoryMaterialData.category == 'Material';

	return (
		<Box
			sx={{
				marginTop: '20px',
				borderRadius: '10px',
				padding: '10px',
				backgroundColor: '#F9FAFB',
				position: 'relative',
				boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.15)',
			}}
		>
			{accessoryData.materials.length > 1 && (
				<IconButton
					onClick={() =>
						setAccessoryData((prev) => {
							console.log(prev.materials);

							prev.materials.splice(index, 1);
							console.log(prev.materials);

							return {
								...prev,
								materials: prev.materials,
							};
						})
					}
					sx={{
						position: 'absolute',
						top: 1,
						right: 2,
						zIndex: '10',
					}}
					size="small"
				>
					<DeleteOutlineIcon sx={{ color: 'orangeRed' }} />
				</IconButton>
			)}

			<Stack
				display="grid"
				gridTemplateColumns={isMaterial ? '1fr 1fr' : '1fr 1fr'}
				gap={2}
				sx={{}}
			>
				{isMaterial && (
					<Box>
						<InputLabel
							id="accessory-name-label"
							sx={{ fontSize: '12px', color: 'gray.500', marginBottom: '5px' }}
						>
							Material Name
							<EmergencyIcon
								sx={{
									color: 'orangeRed',
									fontSize: '8px',
									marginLeft: '5px',
								}}
							/>
						</InputLabel>

						<TextField
							name="name"
							value={accessoryMaterialData.name}
							onChange={(e) =>
								setAccessoryData((prev) => {
									prev.materials.splice(index, 1, {
										...prev.materials[index],
										name: e.target.value,
									});

									return {
										...prev,
										materials: prev.materials,
									};
								})
							}
						/>
					</Box>
				)}

				<Box>
					<InputLabel
						id="accessory-name-label"
						sx={{ fontSize: '12px', color: 'gray.500', marginBottom: '5px' }}
					>
						{isMaterial ? 'Unit Price' : 'Rate'}

						<EmergencyIcon
							sx={{
								color: 'orangeRed',
								fontSize: '8px',
								marginLeft: '5px',
							}}
						/>
					</InputLabel>

					<TextField
						name="price"
						type="number"
						value={removeStartingZero(accessoryMaterialData.price)}
						onChange={(e) => {
							const value = Number(e.target.value);
							// Prevent negative values
							if (value < 0) return;

							setAccessoryData((prev) => {
								prev.materials.splice(index, 1, {
									...prev.materials[index],
									price: Number(value.toFixed(2)),
								});
								return {
									...prev,
									materials: prev.materials,
								};
							});
						}}
						onInvalid={(e) => {
							e.preventDefault();
						}}
						slotProps={{
							htmlInput: {
								step: '0.01',
								inputMode: 'decimal',
								min: '0', // Prevents negative input
							},
							input: {
								startAdornment: (
									<InputAdornment
										position="start"
										sx={{
											marginLeft: '-10px',
										}}
									>
										<Typography color="gray.500"> $</Typography>
									</InputAdornment>
								),
							},
						}}
					/>
				</Box>

				{isMaterial && (
					<Box>
						<InputLabel
							id="accessory-name-label"
							sx={{ fontSize: '12px', color: 'gray.500', marginBottom: '5px' }}
						>
							Units per Accessory
							<EmergencyIcon
								sx={{
									color: 'orangeRed',
									fontSize: '8px',
									marginLeft: '5px',
								}}
							/>
						</InputLabel>

						<TextField
							name="unitsPerAccessory"
							type="number"
							value={accessoryMaterialData.unitsPerAccessory || ''}
							onInvalid={(e) => {
								e.preventDefault();
							}}
							slotProps={{
								htmlInput: {
									step: 1,
									inputMode: 'numeric',
									min: '0',
								},
							}}
							onChange={(e) => {
								const value = Number(e.target.value);
								// Prevent negative values
								if (value < 0) return;

								setAccessoryData((prev) => {
									prev.materials.splice(index, 1, {
										...prev.materials[index],
										unitsPerAccessory: value,
									});

									return {
										...prev,
										materials: prev.materials,
									};
								});
							}}
						/>
					</Box>
				)}

				<Box>
					<InputLabel
						id="accessory-name-label"
						sx={{ fontSize: '12px', color: 'gray.500', marginBottom: '5px' }}
					>
						{isMaterial ? 'Each unit handles up to' : 'Labor handles up to'}

						<EmergencyIcon
							sx={{
								color: 'orangeRed',
								fontSize: '8px',
								marginLeft: '5px',
							}}
						/>
					</InputLabel>

					<TextField
						name="maxAccessories"
						type="number"
						value={accessoryMaterialData.maxAccessories || ''}
						onChange={(e) => {
							const value = Number(e.target.value);
							// Prevent negative values
							if (value < 0) return;

							setAccessoryData((prev) => {
								prev.materials.splice(index, 1, {
									...prev.materials[index],
									maxAccessories: value,
								});

								return {
									...prev,
									materials: prev.materials,
								};
							});
						}}
						onInvalid={(e) => {
							e.preventDefault();
						}}
						slotProps={{
							htmlInput: {
								step: 1,
								inputMode: 'numeric',
								min: '0',
							},
							input: {
								endAdornment: (
									<InputAdornment
										position="end"
										sx={{
											marginRight: '-10px',
										}}
									>
										<Typography color="gray.500" fontSize="12px">
											{calculationTypesMap[accessoryData.calculationType]}
										</Typography>
									</InputAdornment>
								),
							},
						}}
					/>
				</Box>
			</Stack>
		</Box>
	);
};

export default AccessoryMaterialDataCard;
